import type { PulseModel, PulseCard } from "./pulse";
import type { InsightSeverity } from "./insights";
import type { CrossInsight } from "./cross-insights";
import type { TrendInsight } from "./trend-insights";
import type { PulseClientRow, PulseClientActionRow } from "./pulse-client";
import { computePeriods } from "./date-utils";

export type PulseViewKind = "classic" | "brief" | "queue";

export const PULSE_VIEW_LABELS: Record<PulseViewKind, string> = {
  classic: "Classic view", brief: "The Brief", queue: "Action Queue",
};

export function pulseViewForPath(pathname: string): PulseViewKind | null {
  const match = pathname.match(/^\/clients\/[^/]+\/pulse(?:\/(brief|queue|story|classic))?\/?$/);
  if (!match) return null;
  // Retired Story URLs redirect to Brief.
  return match[1] === "story" ? "brief" : (match[1] as PulseViewKind | undefined) ?? "brief";
}

/** Name the actual comparison window, including its year for year-over-year views. */
export function pulseViewPeriodLabel(range: string, compare: string, referenceDate?: Date): string {
  const periods = computePeriods(range, compare === "none" ? "period" : compare, referenceDate);
  const formatter = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" });
  const label = (period: { startDate: string; endDate: string }) => formatter.formatRange(
    new Date(`${period.startDate}T12:00:00`), new Date(`${period.endDate}T12:00:00`),
  );
  return `${label(periods.current)} · vs. ${label(periods.previous)}`;
}

/** A single reviewable signal; keep its original evidence and stable mute/task id. */
export interface PulseFocusItem {
  id: string;
  title: string;
  summary: string;
  provider: string;
  providerLabel: string;
  theme: string;
  severity: InsightSeverity;
  significance: number;
  estimated: boolean;
  action: PulseCard["action"];
  evidence:
    | { kind: "metric"; card: PulseCard }
    | { kind: "connection"; connection: CrossInsight }
    | { kind: "trend"; trend: TrendInsight }
    | { kind: "client"; row: PulseClientRow | PulseClientActionRow };
}

function rank(item: PulseFocusItem): number {
  // Trend significance uses weeks * 10, unlike the bounded metric scale.
  return Math.min(100, item.significance) + (item.severity === "concern" ? 8 : item.severity === "warning" ? 4 : 0);
}

export function pulseFocusItems(model: PulseModel): PulseFocusItem[] {
  const items: PulseFocusItem[] = [];
  // Client curation is a boundary: never reconstruct the full team board or
  // attach internal action recommendations to a curated client row.
  if (model.audience === "client") {
    for (const row of [...(model.clientSections?.wins ?? []), ...(model.clientSections?.workingOn ?? [])]) {
      items.push({
        id: row.id, title: row.label, summary: row.sentence, provider: row.provider,
        providerLabel: row.providerLabel, theme: row.theme, severity: row.severity,
        significance: row.significance, estimated: row.theme === "Opportunities & Market",
        action: null, evidence: { kind: "client", row },
      });
    }
  } else {
    for (const section of [...(model.trendsSection ? [model.trendsSection] : []), ...model.sections]) {
      for (const entry of [...section.needsAttention, ...section.wins]) {
        const common = { theme: section.title, estimated: section.estimated };
        if (entry.kind === "source") {
          for (const card of entry.source.cards) {
            const i = card.insight;
            items.push({ ...common, id: i.id, title: i.metricLabel, summary: i.message,
              provider: i.provider, providerLabel: i.providerLabel, severity: i.severity,
              significance: i.significance, action: card.action, evidence: { kind: "metric", card } });
          }
        } else if (entry.kind === "connection") {
          const c = entry.connection;
          items.push({ ...common, id: c.id, title: c.title, summary: c.summary,
            provider: c.providers[0] ?? "cross", providerLabel: "Cross-channel",
            severity: c.severity, significance: c.significance, action: null,
            evidence: { kind: "connection", connection: c } });
        } else {
          const t = entry.trend;
          items.push({ ...common, id: t.id, title: t.title, summary: t.summary,
            provider: t.provider ?? "trend", providerLabel: "Trends over time",
            severity: t.severity, significance: entry.significance, action: null,
            evidence: { kind: "trend", trend: t } });
        }
      }
    }
  }
  return [...new Map(items.map(item => [item.id, item])).values()]
    .sort((a, b) => rank(b) - rank(a) || a.id.localeCompare(b.id));
}

/** Brief: lead with the most material concern, then preserve breadth. */
export function selectBriefItems(items: PulseFocusItem[]): PulseFocusItem[] {
  const lead = items.find(i => i.severity !== "win") ?? items[0];
  if (!lead) return [];
  const selected = [lead];
  const win = items.find(i => i.severity === "win" && i.id !== lead.id);
  if (win) selected.push(win);
  const next = items.find(i => !selected.includes(i) && i.provider !== lead.provider)
    ?? items.find(i => !selected.includes(i));
  if (next) selected.push(next);
  return selected;
}

export function pulseItemAction(item: PulseFocusItem): string | null {
  if (item.evidence.kind === "client") {
    return "action" in item.evidence.row ? item.evidence.row.action.text : null;
  }
  return item.action?.action ?? null;
}
